import { useCallback, useEffect, useRef } from "react";
import { TextPanel } from "..";
import { useMetaVariableStore } from "../../../../store";
import styles from "../AdvancedPanel.module.css";
import { useAdvancedPanelTimeline } from "../useAdvancedPanelTimeline";
import { TimelineButton } from "./TimeLineButton";
import { useObjectsState } from "../../../../../../contexts/ObjectsProvider";
import { useTimeline } from "../../../../../../contexts/TimelineProvider/TimelineProvider";
import { Collapse } from "./Collapse";
import useCollapseState from "../../../../../../hooks/useCollapseState";
import { FloatingTextEditor } from "../../FloatingTextEditor";

const DEBOUNCE_MS = 300;

interface TextProps {
  data: TextPanel;
  onChange: () => void;
  onFocus: (key: string) => void;
  active: boolean;
}

export function Text({ data, onChange, onFocus, active }: Readonly<TextProps>) {
  const value = useMetaVariableStore((s) => s.metaVariables[data.key]);
  const metaData = useMetaVariableStore((s) => s.metaVariablesData[data.key]);
  const setMetadata = useMetaVariableStore((s) => s.setMetaData);
  const set = useMetaVariableStore((s) => s.setMetaVariable);
  const upsertAnimatedMetaVariable = useMetaVariableStore((s) => s.upsertAnimatedMetaVariable);
  const [tl] = useTimeline();
  const currentTime = tl?.scrubbingCurrentTime;
  const selectedObjectId = useObjectsState().selectedObjects[0]?.objectId;
  const { isObjectInTimeline, handleTimelineClick, metaVariableInCurrentTime } = useAdvancedPanelTimeline(data.key);
  const { isOpen, handleCollapseToggle, elementRef } = useCollapseState(data.key);

  const handleToggle = (newState: boolean) => {
    handleCollapseToggle(newState);
  };

  const onTimelineClick = () => {
    handleTimelineClick(value);
    onChange();
  };

  const handleHideClick = () => {
    setMetadata(data.key, "hide", !metaData?.hide);
    onChange();
  };

  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const latestTextRef = useRef<string>("");

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleChange = useCallback(
    (newText: string) => {
      set(data.key, newText);
      latestTextRef.current = newText;

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (isObjectInTimeline) {
          upsertAnimatedMetaVariable(data.key, currentTime, latestTextRef.current, selectedObjectId);
        }
        onChange();
      }, DEBOUNCE_MS);
    },
    [data.key, set, isObjectInTimeline, upsertAnimatedMetaVariable, currentTime, selectedObjectId, onChange],
  );

  const setSelectedFont = (font: string) => {
    setMetadata(data.key, "selectedFont", font);
  };

  return (
    <Collapse title={data.name} id={data.key} isOpen={isOpen} onToggle={handleToggle}>
      <div ref={elementRef} id={`panel-${data.key}`} className={styles.group} key={data.name}>
        <div className={styles.timelineWrapper}>
          <div className={styles.selectWrapper}>
            <div className={styles.textGroup}>
              <FloatingTextEditor
                value={value as string}
                onChange={handleChange}
                id={data.key}
                onFocus={() => onFocus(data.key)}
                active={active}
                font={metaData?.font ?? "sans-serif"}
                setSelectedFont={setSelectedFont}
              />
            </div>
            <div className={styles.checkboxWrapper}>
              <input type="checkbox" checked={!metaData?.hide} onChange={handleHideClick} />
              <p>Hide</p>
            </div>
          </div>
          {isObjectInTimeline && <TimelineButton onClick={onTimelineClick} active={!!metaVariableInCurrentTime} />}
        </div>
      </div>
    </Collapse>
  );
}
